import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench, PackageSearch, ClipboardCheck, AlarmClockOff, Send, Plus } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { StatCard } from "@/components/ui/stat-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { binderyApi, type BinderyDashboardStats } from "@/lib/api/bindery";
import { formatMoney, formatRelativeTime } from "./binderyFormatters";

export default function BinderyDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<BinderyDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    binderyApi.dashboard
      .stats()
      .then((s) => alive && setStats(s))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Bindery & Repair"
        description="Track damaged materials through repair — from flagging to re-shelving."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Bindery" }]}
        showSearch={false}
        titleSize="md"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/bindery/requests">All requests</Link>
            </Button>
            <Button onClick={() => navigate("/bindery/requests/new")}>
              <Plus className="mr-2 h-4 w-4" />
              New request
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard
          icon={<Wrench className="h-5 w-5" />}
          label="Open requests"
          value={loading ? "—" : (stats?.totals.openRequests ?? 0)}
          iconBg="bg-blue-100 text-blue-700"
        />
        <StatCard
          icon={<PackageSearch className="h-5 w-5" />}
          label="Items at bindery"
          value={loading ? "—" : (stats?.totals.itemsAtBindery ?? 0)}
          iconBg="bg-purple-100 text-purple-700"
        />
        <StatCard
          icon={<ClipboardCheck className="h-5 w-5" />}
          label="Awaiting QC"
          value={loading ? "—" : (stats?.totals.itemsAwaitingQc ?? 0)}
          iconBg="bg-amber-100 text-amber-700"
        />
        <StatCard
          icon={<AlarmClockOff className="h-5 w-5" />}
          label="Overdue batches"
          value={loading ? "—" : (stats?.totals.overdueBatches ?? 0)}
          iconBg="bg-red-100 text-red-700"
        />
        <StatCard
          icon={<Send className="h-5 w-5" />}
          label="Sent this month"
          value={loading ? "—" : (stats?.totals.batchesSentThisMonth ?? 0)}
          iconBg="bg-emerald-100 text-emerald-700"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top vendors — this year</CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.topVendors ?? []).length === 0 ? (
              <p className="text-sm text-slate-500">No vendor activity yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats!.topVendors.map((v) => (
                  <li key={v.vendorId} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-medium text-slate-800">{v.vendorName}</span>
                    <span className="flex items-center gap-4 text-slate-600">
                      <span>
                        {v.batches} batch{v.batches !== 1 ? "es" : ""}
                      </span>
                      <span className="tabular-nums">{formatMoney(v.totalSpent)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent activity</CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.recentActivity ?? []).length === 0 ? (
              <p className="text-sm text-slate-500">No activity yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats!.recentActivity.map((e) => (
                  <li key={e.id} className="py-2 text-sm">
                    <Link
                      to={`/bindery/requests/${e.requestItem.request.id}`}
                      className="font-mono text-primary hover:underline"
                    >
                      {e.requestItem.request.requestNumber}
                    </Link>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="text-slate-700">
                      {e.requestItem.item.barcode} → {e.toStatus.replace(/_/g, " ")}
                    </span>
                    <span className="ml-2 text-xs text-slate-500">
                      {formatRelativeTime(e.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
